import { useEffect, useRef, useState } from "react";

export const TURNSTILE_SITE_KEY = "0x4AAAAAAE2P_UxvCNJGj4mo";

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
      remove: (id?: string) => void;
    };
  }
}

let scriptPromise: Promise<void> | null = null;

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => {
      scriptPromise = null;
      reject(new Error("Failed to load Turnstile"));
    };
    document.head.appendChild(s);
  });
  return scriptPromise;
}

/**
 * Cloudflare Turnstile widget. Stores the token in a hidden input named
 * "turnstile_token" so plain FormData reads keep working, and reports the
 * token upward so the submit button can stay disabled until it exists.
 */
export function Turnstile({
  theme = "dark",
  language,
  resetSignal = 0,
  onToken,
}: {
  theme?: "dark" | "light";
  language?: string;
  resetSignal?: number;
  onToken?: (token: string) => void;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<string | null>(null);
  const [token, setToken] = useState("");

  const set = (t: string) => {
    setToken(t);
    onToken?.(t);
  };

  useEffect(() => {
    let cancelled = false;
    loadTurnstile()
      .then(() => {
        if (cancelled || !hostRef.current || !window.turnstile) return;
        if (widgetId.current) return;
        widgetId.current = window.turnstile.render(hostRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme,
          language,
          callback: (t: string) => set(t),
          "expired-callback": () => set(""),
          "error-callback": () => set(""),
        });
      })
      .catch(() => {
        /* network blocked — token stays empty, server rejects */
      });
    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) {
        window.turnstile.remove(widgetId.current);
        widgetId.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!resetSignal) return;
    if (widgetId.current && window.turnstile) {
      window.turnstile.reset(widgetId.current);
      set("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSignal]);

  return (
    <div>
      <div ref={hostRef} />
      <input type="hidden" name="turnstile_token" value={token} readOnly />
    </div>
  );
}

export function turnstilePayload(fd: FormData) {
  const started = Number(fd.get("form_started_at") || 0);
  return {
    turnstile_token: String(fd.get("turnstile_token") || ""),
    elapsed_ms: started ? Date.now() - started : 0,
  };
}

/** Hidden field that records when the form was mounted (elapsed_ms check). */
export function FormStartedAt() {
  const startedAt = useRef(Date.now());
  return <input type="hidden" name="form_started_at" value={startedAt.current} />;
}
